/**
 * Pure utilities for `leave_types_panel.tsx` — validations, option lists,
 * draft conversion and error-key resolution. Extracted because the panel is
 * >150 lines, per the component-testability rule.
 */
import type {
  LeaveAccrualMethod,
  LeaveType,
  LeaveTypeUpsertFailure,
  UpsertLeaveTypeInput,
} from '../../data/use_leave_types';
import type { StyledSelectOption } from '@jumpstone/ui-react';
import type { TFunction } from '@mukil/i18n';

/**
 * Maps to the backend's `accrual_method` union for `payroll.upsertLeaveType`:
 *  - `fixed_annual`  — the yearly allowance is granted up-front each leave year
 *  - `monthly`       — the yearly allowance accrues in twelve monthly credits
 *  - `none`          — no automatic accrual / unlimited policy
 *  - `statutory`     — country-legislated accrual (computed by the backend)
 */
export type AcrualMethod = LeaveAccrualMethod;

/** Carry-forward mode as the form presents it. */
export type CarryForwardMode = 'unlimited' | 'capped';

export interface LeaveTypeDraft {
  code: string;
  name: string;
  is_paid: boolean;
  tracks_balance: boolean;
  accrual_method: AcrualMethod;
  annual_entitlement_days_scaled: number | null;
  prorate_on_join: boolean;
  carry_forward_mode: CarryForwardMode;
  /** Days ×100; only meaningful when `carry_forward_mode === 'capped'`. */
  carry_forward_max_days_scaled: number | null;
  leave_year_start_month: number;
}

export function make_empty_draft(): LeaveTypeDraft {
  return {
    code: '',
    name: '',
    is_paid: true,
    tracks_balance: true,
    accrual_method: 'fixed_annual',
    annual_entitlement_days_scaled: null,
    prorate_on_join: true,
    carry_forward_mode: 'unlimited',
    carry_forward_max_days_scaled: null,
    leave_year_start_month: 1,
  };
}

/** Seed an edit draft from a stored leave type. */
export function draft_from_leave_type(lt: LeaveType): LeaveTypeDraft {
  return {
    code: lt.code,
    name: lt.name,
    is_paid: lt.is_paid,
    tracks_balance: lt.tracks_balance,
    accrual_method: lt.accrual_method,
    annual_entitlement_days_scaled: lt.annual_entitlement_days_scaled,
    prorate_on_join: lt.prorate_on_join,
    carry_forward_mode:
      lt.carry_forward_max_days_scaled === null ? 'unlimited' : 'capped',
    carry_forward_max_days_scaled: lt.carry_forward_max_days_scaled,
    leave_year_start_month: lt.leave_year_start_month,
  };
}

/** True for methods whose yearly allowance the entitlement engine grants. */
export function is_policy_method(method: AcrualMethod): boolean {
  return method === 'fixed_annual' || method === 'monthly';
}

/** Build the upsert payload from a validated draft. */
export function draft_to_upsert_input(
  draft: LeaveTypeDraft,
): UpsertLeaveTypeInput {
  const policy = is_policy_method(draft.accrual_method);
  return {
    code: draft.code,
    name: draft.name,
    is_paid: draft.is_paid,
    tracks_balance: draft.tracks_balance,
    accrual_method: draft.accrual_method,
    annual_entitlement_days_scaled: policy
      ? draft.annual_entitlement_days_scaled
      : null,
    prorate_on_join: draft.prorate_on_join,
    carry_forward_max_days_scaled:
      draft.carry_forward_mode === 'capped'
        ? (draft.carry_forward_max_days_scaled ?? 0)
        : null,
    leave_year_start_month: draft.leave_year_start_month,
  };
}

/** Parse a "days" text field into ×100 days; `null` for blank / invalid. */
export function parse_days_scaled(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const days = Number(trimmed);
  if (!Number.isFinite(days)) return null;
  return Math.round(days * 100);
}

/** Format ×100 days back into the text field's value. */
export function format_days_input(scaled: number | null): string {
  return scaled !== null ? String(scaled / 100) : '';
}

export function accrual_method_options(t: TFunction): StyledSelectOption[] {
  return [
    {
      value: 'fixed_annual',
      label: t('settings.leave_types.accrual_fixed_annual'),
    },
    {
      value: 'monthly',
      label: t('settings.leave_types.accrual_monthly'),
    },
    {
      value: 'none',
      label: t('settings.leave_types.accrual_none'),
    },
    {
      value: 'statutory',
      label: t('settings.leave_types.accrual_statutory'),
    },
  ];
}

export function carry_forward_options(t: TFunction): StyledSelectOption[] {
  return [
    {
      value: 'unlimited',
      label: t('settings.leave_types.carry_forward_unlimited'),
    },
    {
      value: 'capped',
      label: t('settings.leave_types.carry_forward_capped'),
    },
  ];
}

/** One-line policy summary shown on a collapsed leave-type row. */
export function policy_summary(lt: LeaveType, t: TFunction): string | null {
  if (!lt.tracks_balance || !is_policy_method(lt.accrual_method)) return null;
  if (lt.annual_entitlement_days_scaled === null) {
    return t('settings.leave_types.summary_not_configured');
  }
  const days = lt.annual_entitlement_days_scaled / 100;
  return lt.accrual_method === 'monthly'
    ? t('settings.leave_types.summary_monthly', { days })
    : t('settings.leave_types.summary_fixed', { days });
}

export function validate_leave_type_draft(
  draft: LeaveTypeDraft,
  t: TFunction,
): Partial<Record<keyof LeaveTypeDraft, string>> {
  const errors: Partial<Record<keyof LeaveTypeDraft, string>> = {};

  if (!draft.code.trim()) {
    errors.code = t('settings.leave_types.error_code_required');
  } else if (!/^[a-z0-9_]{1,32}$/.test(draft.code)) {
    errors.code = t('settings.leave_types.error_code_format');
  }

  if (!draft.name.trim()) {
    errors.name = t('settings.leave_types.error_name_required');
  }

  if (
    is_policy_method(draft.accrual_method) &&
    (draft.annual_entitlement_days_scaled === null ||
      draft.annual_entitlement_days_scaled < 0 ||
      draft.annual_entitlement_days_scaled > 36_600)
  ) {
    errors.annual_entitlement_days_scaled = t(
      'settings.leave_types.error_entitlement_required',
    );
  }

  if (
    draft.carry_forward_mode === 'capped' &&
    (draft.carry_forward_max_days_scaled === null ||
      draft.carry_forward_max_days_scaled < 0 ||
      draft.carry_forward_max_days_scaled > 36_600)
  ) {
    errors.carry_forward_max_days_scaled = t(
      'settings.leave_types.error_carry_forward_invalid',
    );
  }

  return errors;
}

export function leave_type_upsert_error_key(
  failure: LeaveTypeUpsertFailure,
): string {
  switch (failure) {
    case 'entitlement_required':
      return 'settings.leave_types.error_entitlement_required';
    case 'forbidden':
      return 'settings.leave_types.error_forbidden';
    case 'not_found':
      return 'settings.leave_types.error_not_found';
    case 'error':
      return 'settings.leave_types.error_save_generic';
  }
}
